import { useKeys, useMapObject } from '@client/hooks/use-object.hooks';
import {
  energyCarrierNames,
  energyConsumerNames,
} from '@shared/helpers/energy/energy-class.model';
import { sum } from '@shared/helpers/math.helpers';
import { getEntries } from '@shared/helpers/object.helpers';
import { entrySortFn } from '@shared/helpers/sort.helpers';
import React from 'react';

import { NodonListItemResult } from '@ui/ListItemResult';
import {
  useHeatedArea,
  useYearlyEnergyConsumptionRecord,
} from '@features/energy/energy.hook';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';

export const EnergyConsumptionGroup: React.FC = () => {
  const energyConsumptionRecord = useYearlyEnergyConsumptionRecord();
  const consumers = useKeys(energyConsumptionRecord);
  const totalByConsumer = useMapObject(energyConsumptionRecord, (consumer) =>
    sum(consumer),
  );
  const heatedArea = useHeatedArea();
  const totalConsumption = sum(totalByConsumer);
  const consumptionPerSqm = heatedArea > 0 ? totalConsumption / heatedArea : 0;
  return (
    <NodonListItemResult
      name="Energy consumption"
      tooltip={`Total: ${getDisplayValueData({ value: totalConsumption, unit: 'kWh' }, { divideBy: { unit: 'yr' } }).full}`}
      value={consumptionPerSqm}
      unit="kWh/m^2"
    >
      {/* Yearly energy consumption per carrier */}
      {consumers
        .filter((consumer) => totalByConsumer[consumer]) // Filter out consumers with no energy consumption
        .map((consumer) => {
          const consumerTotal = totalByConsumer[consumer];
          const consumerPerSqm =
            heatedArea > 0 ? consumerTotal / heatedArea : 0;
          return (
            <NodonListItemResult
              key={consumer}
              name={energyConsumerNames[consumer]}
              tooltip={`Total: ${getDisplayValueData({ value: consumerTotal, unit: 'kWh' }, { divideBy: { unit: 'yr' } }).full}`}
              value={consumerPerSqm}
              unit="kWh/m^2"
              indentation={1}
            >
              {getEntries(energyConsumptionRecord[consumer])
                .filter(([, value]) => value)
                .sort(entrySortFn)
                .map(([carrier, value]) => {
                  const carrierPerSqm = heatedArea > 0 ? value / heatedArea : 0;
                  return (
                    <NodonListItemResult
                      key={carrier}
                      name={energyCarrierNames[carrier]}
                      tooltip={`Total: ${getDisplayValueData({ value: value, unit: 'kWh' }, { divideBy: { unit: 'yr' } }).full}`}
                      value={carrierPerSqm}
                      unit="kWh/m^2"
                      indentation={2}
                    />
                  );
                })}
            </NodonListItemResult>
          );
        })}
    </NodonListItemResult>
  );
};
